import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('whowsell-theme') || 'dark';
    }
    return 'dark';
  });
  const initialRender = useRef(true);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.setAttribute('data-theme', theme);
    localStorage.setItem('whowsell-theme', theme);

    // Clear the inline backgroundColor set by the blocking script
    root.style.backgroundColor = '';
    if (document.body) document.body.style.backgroundColor = '';

    // After initial render, enable smooth transitions for theme toggle only
    if (initialRender.current) {
      initialRender.current = false;
      // Add transition class after a short delay so initial render is instant
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.add('theme-ready');
        });
      });
    }
  }, [theme]);

  const toggleTheme = () => {
    const root = document.documentElement;
    // Disable ALL transitions during theme switch to prevent any flash/delay
    root.classList.add('theme-switching');
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    // Re-enable transitions after the DOM has fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('theme-switching');
      });
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
